import type { ReactNode } from 'react'
import {
  ClientBackground,
  getGuestSurfaceBackgroundClassName,
  shouldRenderGuestMarketingShellBackground,
  tenantHasClientBackgroundDecor,
} from '@/components/backgrounds/client-background'
import { AppearanceToggle } from '@/components/appearance-toggle'
import {
  MarketingPageHeader,
  type MarketingHeaderSurfaceVariant,
} from '@/components/landing/marketing-page-header'
import { useAuth } from '@/hooks/use-auth'
import { useGuestSurfaceTheme, type GuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { useStickyNavScrolled } from '@/hooks/use-sticky-nav-scrolled'
import { isLandingThemeToggleEnabled } from '@/landing/landing-theme-policy'
import { MARKETING_DIVIDER_BORDER } from '@/landing/landing-surfaces'
import { LandingLayoutRoot, MARKETING_STICKY_NAV_CONTENT_INSET } from '@/landing/layouts/landing-layouts'
import { MarketingContentFrame } from '@/landing/marketing-content-frame'
import { cn } from '@/lib/utils'
import type { LandingNavLink } from '@/types/client-landing'

type MarketingPageShellProps = {
  children: ReactNode
  transparentShell?: boolean
  /** Cinematic hero: shell-owned sticky overlay nav (one render path) */
  stickyOverlayHeader?: boolean
  /** Keep sticky overlay nav in solid (scrolled) state — pages without a hero band. */
  stickyHeaderSolid?: boolean
  /** Top inset when fixed sticky nav does not overlay page content (e.g. public share). */
  stickyHeaderContentInset?: boolean
  signInLabel?: string
  registerLabel?: string
  hideRegister?: boolean
  hideSignIn?: boolean
  /** When set (e.g. from PublicLandingPage), avoids a second guest-theme hook instance. */
  guestTheme?: GuestSurfaceTheme
  guestPreference?: 'light' | 'dark' | 'system'
  onGuestThemeChange?: (t: 'light' | 'dark' | 'system') => void
  navLinks?: LandingNavLink[]
  mobileNavCta?: LandingNavLink
  headerBrand?: ReactNode
  headerSurfaceVariant?: MarketingHeaderSurfaceVariant
  footer?: ReactNode
  footerClassName?: string
  /** Optional class on the outer shell (e.g. YesHuman font tokens for nav + body). */
  shellClassName?: string
}

export function MarketingPageShell({
  children,
  transparentShell = false,
  stickyOverlayHeader = false,
  stickyHeaderSolid = false,
  stickyHeaderContentInset = false,
  signInLabel = 'Sign in',
  registerLabel = 'Register',
  hideRegister = false,
  hideSignIn = false,
  guestTheme: guestThemeProp,
  guestPreference: guestPreferenceProp,
  onGuestThemeChange,
  navLinks,
  mobileNavCta,
  headerBrand,
  headerSurfaceVariant,
  footer,
  footerClassName,
  shellClassName,
}: MarketingPageShellProps) {
  const hasDecor = tenantHasClientBackgroundDecor()
  const internalGuestTheme = useGuestSurfaceTheme()
  const guestTheme = guestThemeProp ?? internalGuestTheme.guestTheme
  const guestPreference = guestPreferenceProp ?? internalGuestTheme.guestPreference
  const setGuestTheme = onGuestThemeChange ?? internalGuestTheme.setGuestTheme
  const { ref: scrollRef, scrolled } = useStickyNavScrolled(0)
  const { isAuthenticated, isInitialized } = useAuth()
  /** Gate on init so guest CTAs don't flash before auth loads, then swap to the app CTA. */
  const authenticated = isInitialized && isAuthenticated
  const showThemeToggle = isLandingThemeToggleEnabled()
  const creamSurface = headerSurfaceVariant === 'cream'
  const footerThemeToggle = showThemeToggle ? (
    <div className="md:hidden">
      <AppearanceToggle
        variant="marketing"
        size={creamSurface ? 'compact' : 'default'}
        marketingTheme={guestTheme}
        marketingPreference={guestPreference}
        onMarketingThemeChange={setGuestTheme}
      />
    </div>
  ) : null

  return (
    <LandingLayoutRoot
      className={cn(
        shellClassName,
        'relative min-h-screen',
        headerSurfaceVariant === 'cream'
          ? 'bg-[var(--marketing-bg,var(--background))]'
          : transparentShell
            ? 'bg-transparent'
            : 'bg-background',
        'h-full min-h-0 w-full overflow-x-hidden overscroll-y-contain touch-pan-y'
      )}
    >
      <div className="relative min-h-full w-full">
        {!hasDecor && (
          <div
            className="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-gradient-to-b from-muted/40 via-background to-background dark:from-background dark:via-background dark:to-background"
            aria-hidden
          />
        )}
        {shouldRenderGuestMarketingShellBackground() ? (
          <ClientBackground variant="auth" className={getGuestSurfaceBackgroundClassName()} />
        ) : null}

        <div ref={scrollRef} className="relative z-10 flex min-h-full flex-col">
          <MarketingPageHeader
            sticky={stickyOverlayHeader}
            overlay={stickyOverlayHeader}
            scrolled={stickyHeaderSolid || scrolled}
            authenticated={authenticated}
            signInLabel={signInLabel}
            registerLabel={registerLabel}
            hideRegister={hideRegister}
            hideSignIn={hideSignIn}
            marketingTheme={guestTheme}
            marketingPreference={guestPreference}
            onMarketingThemeChange={setGuestTheme}
            navLinks={navLinks}
            mobileNavCta={mobileNavCta}
            brand={headerBrand}
            surfaceVariant={headerSurfaceVariant}
          />
          <div
            className={cn(
              'flex-1',
              stickyOverlayHeader && stickyHeaderContentInset && MARKETING_STICKY_NAV_CONTENT_INSET
            )}
          >
            {children}
          </div>

          {footer ? (
            <footer
              className={cn(
                'shrink-0 border-t py-6 text-sm',
                creamSurface
                  ? 'border-[var(--marketing-border,#e9e3d6)] text-[var(--marketing-muted,#667078)]'
                  : cn('text-muted-foreground', MARKETING_DIVIDER_BORDER),
                footerClassName
              )}
            >
              <MarketingContentFrame
                className={cn(
                  'flex items-center gap-4',
                  creamSurface
                    ? 'justify-start max-md:justify-between'
                    : 'justify-center max-md:justify-between',
                )}
              >
                <div className="min-w-0">{footer}</div>
                {footerThemeToggle}
              </MarketingContentFrame>
            </footer>
          ) : null}
        </div>
      </div>
    </LandingLayoutRoot>
  )
}
